import React, { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  benefitType: string;
  company: string;
  startDate: string;
  current: boolean;
  endDate: string;
  payFrequency: string;
  monthlyNetIncome: number;
  onBenefitTypeChange: (val: string) => void;
  onCompanyChange: (val: string) => void;
  onStartDateChange: (val: string) => void;
  onCurrentChange: (val: boolean) => void;
  onEndDateChange: (val: string) => void;
  onPayFrequencyChange: (val: string) => void;
  onMonthlyIncomeChange: (val: number) => void;
}

const payFrequencyOptions = [
  "daily",
  "weekly",
  "bi-weekly",
  "monthly",
  "varies",
] as const;

const benefitOptions = [
  { value: "social-security", labelKey: "income.benefits.socialSecurity" },
  { value: "unemployment", labelKey: "income.benefits.unemployment" },
  { value: "assistance", labelKey: "income.benefits.assistance" },
  { value: "alimony", labelKey: "income.benefits.alimony" },
  { value: "pension", labelKey: "income.benefits.pension" },
  { value: "trust", labelKey: "income.benefits.trust" },
  { value: "other", labelKey: "income.benefits.other" },
];

export const IncomeBenefitsFields: React.FC<Props> = ({
  benefitType,
  company,
  startDate,
  current,
  endDate,
  payFrequency,
  monthlyNetIncome,
  onBenefitTypeChange,
  onCompanyChange,
  onStartDateChange,
  onCurrentChange,
  onEndDateChange,
  onPayFrequencyChange,
  onMonthlyIncomeChange,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* What type of benefit/income is this? */}
      <div className="mb-4">
        <label htmlFor="benefitType" className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("income.benefits.benefitTypeLabel")}{" "}
          <span className="text-danger">*</span>
        </label>
        <select
          id="benefitType"
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
          value={benefitType}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            onBenefitTypeChange(e.target.value)
          }
          required
        >
          <option value="">{t("income.benefits.selectBenefitType")}</option>
          {benefitOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>
      </div>

      {/* Who provides this income? */}
      <div className="mb-4">
        <label htmlFor="company" className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("income.benefits.providerLabel")}{" "}
          <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="company"
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
          value={company}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onCompanyChange(e.target.value)
          }
          required
        />
      </div>

      {/* When did you start receiving this income? */}
      <div className="mb-4">
        <label htmlFor="startDate" className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("income.benefits.startDateLabel")}{" "}
          <span className="text-danger">*</span>
        </label>
        <input
          type="date"
          id="startDate"
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
          value={startDate}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onStartDateChange(e.target.value)
          }
          required
        />
      </div>

      {/* Still receiving income? */}
      <fieldset className="mb-4">
        <legend className="mb-2 block text-sm font-medium text-gray-700">
          {t("income.benefits.currentLegend")}{" "}
          <span className="text-danger">*</span>
        </legend>
        <div className="flex items-center gap-2">
          <input
            className="h-4 w-4 border-gray-300 text-primary focus:ring-primary/20"
            type="radio"
            name="benefitsCurrent"
            id="benefitsCurrentYes"
            value="yes"
            checked={current === true}
            onChange={() => onCurrentChange(true)}
            required
          />
          <label className="text-sm text-gray-700" htmlFor="benefitsCurrentYes">
            {t("income.benefits.yes")}
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            className="h-4 w-4 border-gray-300 text-primary focus:ring-primary/20"
            type="radio"
            name="benefitsCurrent"
            id="benefitsCurrentNo"
            value="no"
            checked={current === false}
            onChange={() => onCurrentChange(false)}
          />
          <label className="text-sm text-gray-700" htmlFor="benefitsCurrentNo">
            {t("income.benefits.no")}
          </label>
        </div>
      </fieldset>

      {/* How often do you receive payments? */}
      <div className="mb-4">
        <label htmlFor="payFrequency" className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("income.benefits.payFrequencyLabel")}{" "}
          <span className="text-danger">*</span>
        </label>
        <select
          id="payFrequency"
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
          value={payFrequency}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            onPayFrequencyChange(e.target.value)
          }
          required
        >
          <option value="">{t("income.benefits.selectPayFrequency")}</option>
          {payFrequencyOptions.map((freq) => (
            <option key={freq} value={freq}>
              {t(`income.payFrequency.${freq}`)}
            </option>
          ))}
        </select>
      </div>

      {/* On average, monthly net income */}
      <div className="mb-4">
        <label htmlFor="monthlyNetIncome" className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("income.benefits.monthlyIncomeLabel")}{" "}
          <span className="text-danger">*</span>
        </label>
        <div className="flex items-center rounded-lg border border-gray-300">
          <span className="border-r border-gray-300 bg-gray-50 px-3 py-2.5 text-sm text-gray-500">$</span>
          <input
            type="number"
            id="monthlyNetIncome"
            className="w-full rounded-r-lg border-0 px-4 py-2.5 text-sm transition-all focus:ring-2 focus:ring-primary/20 focus:outline-none"
            value={monthlyNetIncome}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onMonthlyIncomeChange(parseFloat(e.target.value) || 0)
            }
            step="0.01"
            required
          />
        </div>
      </div>

      {/* End Date */}
      {!current && (
        <div className="mb-4">
          <label htmlFor="endDate" className="mb-1.5 block text-sm font-medium text-gray-700">
            {t("income.benefits.endDateLabel")}
          </label>
          <input
            type="date"
            id="endDate"
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
            value={endDate}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onEndDateChange(e.target.value)
            }
          />
        </div>
      )}
    </>
  );
};
